import { SlotIt, slottedMarker, type CardButton, type CardComponent } from "@raventic/sdk";
import { joinClasses } from "@raventic/sdk/helpers";
import { buildCardView, type RcCardView } from "./view.ts";

// rcobchod's own listing card (`source/card.html`, class names verbatim) in the LIGHT DOM, for the
// search results and the similarity carousel: the whole card is wrapped in `SlotIt`, so the shop's
// stylesheet (produkt.css `.product.box`, stitky_color.css labels, bootstrap buttons + icons) and its
// document-level JS delegation (`.compare-trigger`, `.copy-btn`) reach it. Data mapping: CARD.md.
//
// Clicks: the site's three links to the detail (image, title, "Zobrazit detail") each carry the
// primary-action props, so each tracks + navigates. The compare, coupon-copy and cart buttons sit
// outside those links and carry the slotted marker: their clicks go to the site's handlers / the
// SDK's cart handler, never to product-click tracking.
//
// Sale products: the discount badge (`.tagsTop .procenta`) and the struck-through price before the
// sale (`.cena .event`); the description bullets (`.popis`) from the `benefit` params. Only some
// products have bullets, so every card stays `raventic-rc-card--compact` (light.scss): the site's
// fixed card and caption heights give way to the content, and cards with and without bullets line
// up by stretching to their row. Dropped on purpose: the hidden inputs of the site's `/basket.php`
// form (the SDK cart posts itself, cart.ts) and lazysizes' `lazyload` class (our image loads natively).

const Labels = ({ labels }: { labels: RcCardView["labels"] }) => (
  // Always rendered: the site keeps the empty row (its top border separates image and title).
  <div className="tags">
    {labels.map((label) => (
      <div key={label.className} className={`label label-primary ${label.className}`}>
        <span>{label.text}</span>
      </div>
    ))}
  </div>
);

// The site's description bullets (`.popis ul`: line-clamped items, the box cut at 78px).
const Benefits = ({ benefits }: { benefits: string[] }) => (
  <div className="popis">
    <ul>
      {benefits.map((benefit) => (
        <li key={benefit}>{benefit}</li>
      ))}
    </ul>
  </div>
);

const Coupon = ({ coupon }: { coupon: NonNullable<RcCardView["coupon"]> }) => (
  <div className="nakup-jeste2-wrapper">
    <div className="nakup-jeste2 C">
      <span className="kupon-text">
        <strong>{coupon.price}</strong> s kódem {coupon.code}
      </span>
      <button type="button" className="copy-btn" data-copy={coupon.code} aria-label="Zkopírovat kód" {...slottedMarker}>
        <i className="bi bi-copy" />
      </button>
    </div>
  </div>
);

// The cart icon button (the site's `btn btn-primary cart3`), driven by the SDK: `getProps()` brings
// the click handler (cart.ts), the pending `disabled` state and the title. Any other action is
// already covered by the "Zobrazit detail" link next to it.
const CartButton = ({ button }: { button: CardButton }) =>
  button.action !== "cart" ? null : (
    <button className="btn btn-primary cart3" data-action="buy" aria-label={button.label} {...button.getProps()} {...slottedMarker}>
      <i className="bi bi-cart3" aria-hidden="true" />
    </button>
  );

export const RcobchodCard: CardComponent = ({ product, format, actions }) => {
  const view = buildCardView(product, format);
  const primary = actions.getPrimaryActionProps();

  return (
    <SlotIt>
      <div className="box product raventic-rc-card--compact" data-lb-id={view.id}>
        <div className="outer-wrapper">
          <div className="thumbnail">
            <div className="tagsTop">
              {view.discountPercent !== null && <div className="procenta">{view.discountPercent}</div>}
              <button type="button" className="porovnani compare-trigger  border-0" title="Přidat do porovnání" data-product-id={view.id} {...slottedMarker} />
            </div>
            <a {...primary} className="nahled" title={view.name}>
              <img src={view.imageUrl} alt={view.name} loading="lazy" />
            </a>
          </div>
          <div className="inner-wrapper">
            <Labels labels={view.labels} />
            <h3 className="text-center product-v-title">
              <a {...primary} title={`Detail ${view.name}`}>
                {view.name}
              </a>
            </h3>
            <div className="caption">
              {view.benefits.length > 0 && <Benefits benefits={view.benefits} />}
              {view.coupon !== null && <Coupon coupon={view.coupon} />}
            </div>
            <div className="bottom-block">
              <div className="cena">
                {view.oldPrice !== null && (
                  <span className="event">
                    <span>{view.oldPrice}</span>
                  </span>
                )}
                {view.price !== null && (
                  <span className="real-price">
                    <span>{view.price}</span>
                  </span>
                )}
              </div>
              <div className="box-form-group">
                <div>
                  <a {...primary} title={view.detailLabel} className="btn-primary">
                    {view.detailLabel}
                  </a>
                  {actions.button && <CartButton button={actions.button} />}
                </div>
              </div>
              {view.availability !== null && (
                <div className={joinClasses("dostupnost", view.availability.tone === "in-stock" ? "skladem" : "raventic-rc-availability--other")}>
                  <span title={view.availability.text}>{view.availability.text}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SlotIt>
  );
};
